import React, { useEffect, useState } from "react";
import { Button, H3, PerexBox, SystemMessage, Text } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Accordion } from "../../elements/Accordion";
import { PrimaryColor } from "../../elements/AdvancedSetup/PrimaryColor";
import { CategorySelectionWrapper } from "../../elements/CategoriesSelection";
import { BackIcon, Icon, NextIcon } from "../../elements/Icons";
import GoogleWizardHeader from "../../elements/MetaWizard/GoogleWizardHeader";
import { ProgressStepper, StepsT } from "../../elements/ProgressStepper";
import { RemainingIndicator } from "../../elements/RemainingIndicator";
import { SmallLoader } from "../../elements/SmallLoader";
import { SuggestionInputForm } from "../../elements/SuggestionInputForm";
import WizardHeader from "../../elements/WizardHeader";
import {
  WizardStepT,
  useLastImageSuggestionRequestsQuery,
  useLastSuggestionRequestQuery,
  useRequestNewImageSuggestionMutation,
  useRequestNewSuggestionMutation,
} from "../../graphql/generated/graphql";
import { useSetWizardStepEffect } from "./useSetWizardStepEffect";

type SuggestionGeneratorPropsT = {
  hasMetaCampaignsEnabled: boolean;
};

export const SuggestionGenerator = ({ hasMetaCampaignsEnabled }: SuggestionGeneratorPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Suggestion Generator Page" });
  const { t: tSettings } = useTranslation("translation", { keyPrefix: "Settings Page" });
  useSetWizardStepEffect({ wizardStep: WizardStepT.SuggestionGeneratorT });
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [hasCompletedPolling, setHasCompletedPolling] = useState(false);
  const [sendingRequests, setSendingRequests] = useState(false);
  const [formState, setFormState] = useState<{
    disabledSubmit: boolean;
    handleSubmit: () => void;
    userInputLength: number;
  } | null>(null);

  const {
    data: lastSuggestionRequestData,
    error,
    loading: lastSuggestionRequestLoading,
  } = useLastSuggestionRequestQuery({
    fetchPolicy: "network-only",
  });

  const {
    data: imageSuggestionData,
    startPolling,
    stopPolling,
  } = useLastImageSuggestionRequestsQuery({
    skip: !started || hasCompletedPolling,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [requestImageSuggestion] = useRequestNewImageSuggestionMutation();
  const [requestSuggestion] = useRequestNewSuggestionMutation();

  const handleNextStep = async (userInput: string) => {
    setSendingRequests(true);
    setStarted(true);
    await requestImageSuggestion();
    await requestSuggestion({ variables: { userInput } });
  };

  useEffect(() => {
    const request = imageSuggestionData?.organization?.lastImageSuggestionRequests?.[0];
    if (request && !request.inProgress && !hasCompletedPolling) {
      stopPolling();
      setHasCompletedPolling(true);
      navigate("/wizard/image-selection");
    }
  }, [imageSuggestionData, hasCompletedPolling, navigate, stopPolling]);

  useEffect(() => {
    if (started && !hasCompletedPolling) {
      startPolling(1000);
    }
  }, [started, hasCompletedPolling, startPolling]);

  if (lastSuggestionRequestLoading) {
    return (
      <div>
        {hasMetaCampaignsEnabled && <WizardHeader />}
        <GoogleWizardHeader />
        <ProgressStepper currentStep={StepsT.STEP_SUGGESTION} />
        <SmallLoader />
      </div>
    );
  }

  return (
    <div>
      {hasMetaCampaignsEnabled && <WizardHeader />}
      <GoogleWizardHeader />
      <ProgressStepper currentStep={StepsT.STEP_SUGGESTION} />
      <div style={{ display: started ? "none" : "block" }}>
        <PerexBox>{t("InfoBox")}</PerexBox>
        <h2>{t("Sub Headline")}</h2>
        <SuggestionInputForm
          category={lastSuggestionRequestData?.organization?.category}
          disabled={sendingRequests}
          defaultUserInput={
            lastSuggestionRequestData?.organization?.lastSuggestionRequestsShortHeadline?.[0]?.userInput
          }
          hideButton
          onFormStateChange={setFormState}
          onSubmit={handleNextStep}
        />

        <Accordion header={t("Advanced options")}>
          <SystemMessage description={t("Advanced options info box")} level="alert" />
          <div style={{ marginBottom: "16px", marginTop: "32px" }}>
            <H3>{tSettings("Primary Color headline")}</H3>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <Text>{tSettings("Primary Color description")}</Text>
          </div>
          <PrimaryColor />
          <div style={{ marginBottom: "16px", marginTop: "32px" }}>
            <H3>{tSettings("Category selection headline")}</H3>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Category selection description" t={tSettings} />
            </Text>
          </div>
          <CategorySelectionWrapper disabled={sendingRequests} />
        </Accordion>

        <br />
        <br />

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button data-test-id="wizard-back" variant="secondary" onClick={() => navigate("/wizard")}>
            <span style={{ display: "flex", alignItems: "center" }}>
              <BackIcon />
              <span>&nbsp; {t("back")}</span>
            </span>
          </Button>

          <div style={{ display: "flex", alignItems: "center" }}>
            {formState && formState.userInputLength < 350 && (
              <span style={{ color: "var(--app-critical)", marginRight: "20px" }}>{t("Missing information")}</span>
            )}
            <Button
              data-test-id="continue-to-next-step"
              disabled={formState?.disabledSubmit ?? true}
              onClick={() => formState?.handleSubmit()}
            >
              <span style={{ display: "flex", alignItems: "center" }}>
                {t("Continue to next step")}
                &nbsp;
                <NextIcon />
              </span>
            </Button>
          </div>
        </div>
      </div>
      {started && (
        <div style={{ height: "90ch", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ textAlign: "center" }}>
            <Icon kind="loader" />
            <br />
            {t("generation started text")}
          </div>
        </div>
      )}

      {started && <RemainingIndicator defaultSeconds={30} error={!!error} />}
      <br />
      <br />
    </div>
  );
};
